import { ConfiguredTooltipContainer } from './ConfiguredTooltip.elements';
import { ConfiguredTooltipProps, ConfiguredTooltipSettingsProps } from './';
import { RefObject, useEffect, useState } from 'react';
import { LabelWithCircleContainer } from 'common/ui/ChartLegends/LabelWithCircle.elements';
import { formatNumber } from 'common/helpers/formatNumber';
import { convertToZonedTimestamp } from 'common/helpers/dateAndTimeHelpersV2';
 
 

export const detectDataSetChange = (
  el: HTMLDivElement,
  handleChange: (x: Record<string, string>) => void,
  datasets = ['category']
): () => void => {
  const targetNode = el;
  if (!targetNode) return () => ({});

  const prefixed = datasets.map((d) => `data-${d}`);

  const config = {
    attributes: true,
    attributeFilter: prefixed,
    attributeOldValue: true,
  };

  const callback: MutationCallback = (mutationsList) => {
    const changed: Record<string, string> = {};
    for (const mutation of mutationsList) {
      const name = mutation.attributeName
      if (
        mutation.type === 'attributes' &&
        name != null &&
        prefixed.includes(name)
      ) {
        const new_value = (mutation.target as HTMLElement).getAttribute(name);
        if (new_value != null) changed[name.replace(/^data-/, '')] = new_value;
      }
    }
    if (Object.keys(changed).length > 0) handleChange(changed);
  };

  const observer = new MutationObserver(callback);
  observer.observe(targetNode, config);

  // Return a disconnect function to stop observing
  return () => observer.disconnect();
};

export const attachMousePositionListener = (
  el: HTMLElement,
  callback: (position: { clientX: number; clientY: number; pageX: number; pageY: number }) => void
): () => void => {
  const handler = (e: MouseEvent) => {
    if (
      el.classList.contains('is-hidden') &&
      !el.classList.contains('is-visible')
    ) return;
    callback({
      clientX: e.clientX,
      clientY: e.clientY,
      pageX: e.pageX,
      pageY: e.pageY,
    });
  };

  const scrollHandler = () => {
    el.classList.add('is-hidden');
    el.classList.remove('is-visible');
  };

  window.addEventListener('mousemove', handler);
  window.addEventListener('scroll', scrollHandler, true);
  return () => {
    window.removeEventListener('mousemove', handler);
    window.removeEventListener('scroll', scrollHandler, true);
  };
};

let _positionRafId = 0;

const positionTooltip = (
  tooltip_el: HTMLDivElement, 
  datasets = {} as Record<string, string>, 
  {
    sizer_selector = '[data-id="tooltip-sizer"]',
    padding = 10,
    position = 'top'
  }
) => {

  const {
    left,
    top,
  } = datasets
 
  if (!tooltip_el) return

  cancelAnimationFrame(_positionRafId)
  _positionRafId = requestAnimationFrame(() => {
    const sizer_rect = tooltip_el.querySelector?.(sizer_selector)?.getBoundingClientRect() as DOMRect | null

    const sizer_width = sizer_rect?.width || 0
    const sizer_height = sizer_rect?.height || 0
    const cursorX = Number(left)
    const cursorY = Number(top)
    const cursorGap = 8

    const cursorHitsBox = (bx: number, by: number) =>
      cursorX >= bx && cursorX <= bx + sizer_width &&
      cursorY >= by && cursorY <= by + sizer_height

    const clamp = (bx: number, by: number) => ({
      x: Math.max(padding, Math.min(bx, window.innerWidth - sizer_width - padding)),
      y: Math.max(padding, Math.min(by, window.innerHeight - sizer_height - padding)),
    })

    const prevCursorX = Number(tooltip_el.dataset.prevCursorX ?? cursorX)
    const movingRight = cursorX > prevCursorX
    const movingLeft = cursorX < prevCursorX

    const preferred_order = position === 'side'
      ? movingRight
        ? (['left', 'right', 'above', 'below'] as const)
        : movingLeft
          ? (['right', 'left', 'above', 'below'] as const)
          : (['right', 'left', 'above', 'below'] as const)
      : (['above', 'right', 'left', 'below'] as const)

    const candidate_map = {
      above: { x: cursorX - sizer_width / 2, y: cursorY - sizer_height - padding - cursorGap },
      right: { x: cursorX + padding + cursorGap, y: cursorY - sizer_height / 2 },
      left: { x: cursorX - sizer_width - padding - cursorGap, y: cursorY - sizer_height / 2 },
      below: { x: cursorX - sizer_width / 2, y: cursorY + padding + cursorGap },
    } as const

    const candidates = preferred_order.map((placement) => ({
      placement,
      ...clamp(candidate_map[placement].x, candidate_map[placement].y),
    }))

    // Pick the first candidate the cursor doesn't overlap; default to first preferred candidate
    const chosenIndex = candidates.findIndex(({ x, y }) => !cursorHitsBox(x, y))
    const chosen = candidates[chosenIndex >= 0 ? chosenIndex : 0]
    const placement = chosen.placement

    // Arrow position: relative offset of cursor within the tooltip box
    const arrowX = cursorX - chosen.x
    const arrowY = cursorY - chosen.y

    const arrow_el = tooltip_el.querySelector('[data-id="arrow"]') as HTMLElement | null
    if (arrow_el) {
      // Reset all edges, then set based on placement
      arrow_el.style.left = ''
      arrow_el.style.right = ''
      arrow_el.style.top = ''
      arrow_el.style.bottom = ''

      if (placement === 'above') {
        // Arrow on bottom edge, follow cursor X
        arrow_el.style.left = `${arrowX}px`
        arrow_el.style.bottom = '0'
        arrow_el.style.transform = 'translate(-50%, 50%) rotate(45deg)'
      } else if (placement === 'below') {
        // Arrow on top edge, follow cursor X
        arrow_el.style.left = `${arrowX}px`
        arrow_el.style.top = '0'
        arrow_el.style.transform = 'translate(-50%, -50%) rotate(45deg)'
      } else if (placement === 'right') {
        // Arrow on left edge, follow cursor Y
        arrow_el.style.top = `${arrowY}px`
        arrow_el.style.left = '0'
        arrow_el.style.transform = 'translate(-50%, -50%) rotate(45deg)'
      } else if (placement === 'left') {
        // Arrow on right edge, follow cursor Y
        arrow_el.style.top = `${arrowY}px`
        arrow_el.style.right = '0'
        arrow_el.style.transform = 'translate(50%, -50%) rotate(45deg)'
      }
    }

    tooltip_el.style.transform = `translate(${chosen.x}px, ${chosen.y}px)`
    tooltip_el.style.left = '0'
    tooltip_el.style.top = '0'
    tooltip_el.dataset.prevCursorX = String(cursorX)
  })
} 

 

export const ConfiguredTooltip = ({
  time_axis = true,
  className, 
  position = 'top',
  ...props
}: ConfiguredTooltipProps): JSX.Element => {

  
  const internal_state = useState({})
  
  const {
    time_group,
    timeZone = 'UTC' as string,
    tooltip_ref,
    tooltip_settings,
    chart_type,
    colors,
    color_key,
    grouping_key,
    x_key,
    y_key,
    axis_settings,
    vertical,
  } = props || {}
 
  className = `${className || ``} configured-tooltip ConfiguredTooltip`
  if (chart_type) className += ` ${chart_type}-tooltip`

  const {
    format
  } = tooltip_settings || {}

  let {
    tooltip = {} as ConfiguredTooltipSettingsProps,
    setTooltip
  } = props || {} as ConfiguredTooltipSettingsProps
  
  if (!setTooltip) {
    [tooltip, setTooltip] = internal_state
  }
 
  useEffect(() => {     

    const tooltip_el = (tooltip_ref as RefObject<HTMLDivElement>)?.current
    const is_hidden = tooltip_el?.style?.visibility === 'is-hidden' || tooltip_el?.style?.display === 'none'

    if (!tooltip_el || is_hidden) return

    const cleanup = attachMousePositionListener(tooltip_el, ({ clientX, clientY }) => {
      positionTooltip(tooltip_el, { left: String(clientX), top: String(clientY) }, { sizer_selector: '[data-id="tooltip-sizer"]', position })
    })

    const disconnect = detectDataSetChange(tooltip_el, ({ clientX, clientY, xval, yval, group, index, xpos, ypos }) => {
      positionTooltip(tooltip_el,  { left: String(clientX), top: String(clientY) }, { sizer_selector: '[data-id="tooltip-sizer"]', position })
      if (typeof setTooltip === 'function') {
        setTooltip({ xval, yval, group, data_index: index, xpos, ypos })
      }
    }, ['left', 'top', 'xval', 'yval', 'group', 'xpos', 'ypos','index'])

    return () => { cleanup(); disconnect() }

  }, [props])

  let title = tooltip?.xval || tooltip?.title 

  // time group stuff
  if (axis_settings?.bottom?.scale_type === 'time' || time_group || x_key === 'time_group') {
    if (time_group) {
      const [start, end] = String(title).split('-').map(Number)
      title = `${convertToZonedTimestamp(start, timeZone, 'weekDay')}__${convertToZonedTimestamp(start, timeZone, 'timeLm')}__${convertToZonedTimestamp(end -1, timeZone, 'timeLm')}`
    }
    else title = convertToZonedTimestamp(title, timeZone, 'full')
  }

  title = format?.({ title,  tooltip, props }) || title

  if (typeof title === 'string' && time_group) {
    const [date, start, end] = title.split('__')
    title = 
    <div className='title'>
      <div className='date'>{date}</div>
      <div className='date-range'>{start} - {end}</div>
    </div>
  }

  let item = tooltip?.item as Record<string, unknown> | undefined
  item = format?.({ item, tooltip, props }) || item

  let items = tooltip?.items as JSX.Element | undefined
  items = format?.({ items, tooltip, props }) || items
 
  let show_items = <></> as JSX.Element
  let show_item = <></> as JSX.Element

  if (title === false) title = undefined
  if (typeof title === 'string' || typeof title === 'number') title = <div className='title'>{title}</div>

  let keys = axis_settings?.left?.keys || axis_settings?.right?.keys
  if (vertical) keys = axis_settings?.bottom?.keys || axis_settings?.top?.keys

  if (items) { 
    items = items.sort((a, b) => {
      const a_val = a?.y_val  || a?.[y_key] || 0
      const b_val = b?.y_val || b?.[y_key] || 0
      if (typeof a_val === 'number' && typeof b_val === 'number') return b_val - a_val
      return 0
    })
    const scale_type = axis_settings?.left?.scale_type 
    items?.forEach((item, i) => {

       let is_hovered = false

      const color_id = item?.[String(grouping_key  || '')] || item?.[String(color_key  ||  '')] || 'color'
      const { y_val  } = item as Record<string, unknown>
      
      const group = item?.group || item?.[grouping_key as string]
      let color = item?.color || colors?.[color_id as string] || 'red'
      let value = y_val || item?.[y_key as string] || item?.value  
      const label = group //item?.[grouping_key  || 'group' as string]

      if (tooltip?.y_val === value) is_hovered = true
      if (tooltip?.group && String(tooltip?.group) === String(label)) is_hovered = true

      value = format?.({ item, value: true, index: i, items, tooltip, props }) || scale_type === 'linear' ? formatNumber(value) : value
      color = format?.({ item, color: true, index: i, items, tooltip, props }) || color
      
      let row = format?.({  row: item, index: i, items, tooltip, props })

      if (!label || (!value && !row)) return
 
      row = row || 
      <LabelWithCircleContainer colorr={color as string || undefined } className={is_hovered ? 'tooltip-item hovered' : 'tooltip-item'} data-id='tooltip-item' key={`tooltip-item-${i}`}>
        {label && <div className='item-label' data-id='label'>{label}</div>}
        {value && <div className='sub-text value' data-id='value'>({value})</div>}
      </LabelWithCircleContainer>
    
      show_items = <>
        {show_items}
        {row}
      </>

    })
  } else if (item) {

    if (keys) {
      keys.forEach((key) => {

        const label = key
        const value = item?.[key]
        const is_hovered = ['line'].includes(String(chart_type || '')) ? false : tooltip?.y_val === value
        show_items = <>
        <LabelWithCircleContainer  data-group={String(key)} colorr={colors?.[key] as string || undefined } className={is_hovered ? 'tooltip-item hovered' : 'tooltip-item'} data-id='tooltip-item' key={`tooltip-item-${key}`}>
          {label && <div className='item-label' data-id='label'>{label}</div>}
          {value && <div className='sub-text value' data-id='value'>({value})</div>}
        </LabelWithCircleContainer>
        {show_items}
        </>
      })
    } else { 
    
    const { group, y_key, y_val, color } = item || {}
    const label = group || y_key
    const value = y_val

    show_item = 
    <div className='tooltip-item-only'>
      {label && <div className='item-label' data-id='label'>{label}</div>}
      {value && <div className='sub-text value' data-id='value'>({value})</div>}
    </div>
    }
  }

  const innerHTML = format?.({ innerHTML: true, tooltip, props })

  return <>
    <ConfiguredTooltipContainer className={className} ref={tooltip_ref as RefObject<HTMLDivElement>} data-id='tooltip'>
      <div data-id='tooltip-sizer'>
        <div className='tooltip-content'>
          {title}
          {show_item && <div className='tooltip-items grid'>{show_item}</div>}
          {show_items && <div className='tooltip-items grid'>{show_items}</div>}
          {innerHTML && <div className='tooltip-innerHTML'>{innerHTML}</div>}
        </div>  
      </div>
       <div className='tooltip-arrow' data-id='arrow' />
    </ConfiguredTooltipContainer>

  </>
};

export default ConfiguredTooltip;

 